/**
 * Real GI read routes — `getGiDetail` and `getLineage`.
 *
 * The server's `GET /gi/{id}` returns only
 * `{manifest, signed_entity, contract}`. The UI's `GiRecordDetail`
 * additionally carries `lineage`, `attestations`, `counters`, and
 * `status`. Those are derived/auxiliary:
 *
 *   - `lineage`: fetched from the sibling `/gi/{id}/lineage` route.
 *     The server only carries the `LineageRef` (parent_hash, depth) —
 *     we lift that into a single-entry UI `Lineage` for now. A
 *     future `/gi/{id}/lineage/full` endpoint will return the
 *     complete entry list.
 *   - `attestations`: empty until `/attestations` lands.
 *   - `counters`: zeroed until a server-side counter route exists;
 *     call sites already tolerate zero counters in the mock's
 *     "never-run" state.
 *   - `status`: ['signed'] — the engine only persists signed entities.
 */

import type {
  ApiLineageResponse,
  BehavioralContract,
  EntityId,
  GiStatus,
  Hash256,
  Lineage,
  LineageEntry,
  Manifest,
  PublicKey,
  SignedEntity,
} from '../types';
import { ApiHttpError, apiFetch } from '../http';
import type { GiRecordDetail } from '../mock/handlers';
import { canonicalHash } from '../mock/sign';

interface EntityRecordWire {
  manifest: Manifest;
  signed_entity: SignedEntity;
  contract: BehavioralContract;
}

export async function getGiDetail(root_id: EntityId): Promise<GiRecordDetail | null> {
  let record: EntityRecordWire;
  try {
    record = await apiFetch<EntityRecordWire>(`/gi/${encodeURIComponent(root_id)}`);
  } catch (err) {
    if (err instanceof ApiHttpError && err.notFound) return null;
    throw err;
  }

  const lineage = await fetchLineage(root_id, record.manifest).catch((err) => {
    // A missing-lineage response is unusual on a found entity, but
    // we do not want a null lineage to mask the core detail — fall
    // back to a stub derived from the manifest so the UI still
    // renders the entity.
    if (err instanceof ApiHttpError && err.notFound) {
      return stubLineage(root_id, record.manifest);
    }
    throw err;
  });

  return {
    manifest: record.manifest,
    signed_entity: record.signed_entity,
    contract: record.contract,
    lineage,
    attestations: [],
    counters: { signatures: 1, verifications: 0, executions: 0, forks: 0 },
    status: ['signed'] satisfies GiStatus[] as GiStatus[],
  };
}

export async function getLineage(root_id: EntityId): Promise<Lineage> {
  const record = await apiFetch<EntityRecordWire>(`/gi/${encodeURIComponent(root_id)}`);
  return fetchLineage(root_id, record.manifest);
}

async function fetchLineage(
  root_id: EntityId,
  manifest: Manifest,
): Promise<Lineage> {
  const response = await apiFetch<ApiLineageResponse>(
    `/gi/${encodeURIComponent(root_id)}/lineage`,
  );
  const entry: LineageEntry = lineageEntryFromRef({
    root_id: response.root_id,
    version: response.version,
    parent_hash: response.lineage.parent_hash ?? null,
    depth: response.lineage.depth,
    signer: manifest.author.public_key,
    signed_at: manifest.created_at,
  });
  return { root_id: response.root_id, entries: [entry] };
}

function stubLineage(root_id: EntityId, manifest: Manifest): Lineage {
  const entry: LineageEntry = lineageEntryFromRef({
    root_id,
    version: manifest.version,
    parent_hash: null,
    depth: 0,
    signer: manifest.author.public_key,
    signed_at: manifest.created_at,
  });
  return { root_id, entries: [entry] };
}

function lineageEntryFromRef(input: {
  root_id: EntityId;
  version: string;
  parent_hash: Hash256 | null;
  depth: number;
  signer: PublicKey;
  signed_at: Manifest['created_at'];
}): LineageEntry {
  return {
    index: input.depth,
    root_id: input.root_id,
    version: input.version,
    action: input.parent_hash === null ? 'Genesis' : 'Fork',
    parent_hash: input.parent_hash,
    entry_hash: canonicalHash({
      root_id: input.root_id,
      version: input.version,
      parent: input.parent_hash,
    }),
    signer: input.signer,
    signed_at: input.signed_at,
  };
}
